import React, { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Alert, Box, Button, Chip, CircularProgress, MenuItem, Paper, Snackbar, TextField, Tooltip, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import StorageIcon from '@mui/icons-material/Storage';
import WavesIcon from '@mui/icons-material/Waves';
import { useWarehouses, useSetWarehouseActive, useWarehouseQuota, useDbtProjects, describeDependents } from 'api/warehouse';
import { extractApiError } from 'api/errors';
import ConfirmActionDialog from 'components/common/ConfirmActionDialog';
import useRole from 'hooks/useRole';
import { useTokens } from 'theme';
import { countLabel } from 'utils/ru';
import { PreparationWorkspace } from './DbtProjectDetailPage';

// Storage is a secondary setting of the load hub, not a separate dashboard.
export default function WarehousePage() {
  return <StorageSettingsPanel />;
}

function StorageQuotaControls() {
  const { t } = useTranslation();
  const { data: quota, isLoading, error } = useWarehouseQuota();
  const atLimit = quota && !quota.can_create;
  const limitMessage = quota?.limit !== null && quota?.limit !== undefined
    ? t('data.warehousePlanLimit', { warehouses: countLabel(quota.limit, 'warehouse') })
    : '';

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, alignItems: 'flex-start' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Tooltip title={atLimit ? limitMessage : ''}>
          <span>
            <Button component={RouterLink} to="/console/pipelines/storage/new" variant="outlined" startIcon={<AddIcon />} disabled={isLoading || !!error || !quota?.can_create}>
              {t('data.addWarehouse')}
            </Button>
          </span>
        </Tooltip>
        {quota && quota.limit !== null && <Chip size="small" variant="outlined" color={quota.can_create ? 'default' : 'warning'} label={t('data.valueValueOwnWarehouses', { value1: quota.used, value2: quota.limit })} />}
      </Box>
      {atLimit && limitMessage && <Typography variant="body2" color="text.secondary">{limitMessage}</Typography>}
      {error && <Alert severity="warning">{t('data.workspaceData.quotaUnavailable', { defaultValue: 'Не удалось проверить лимит хранилищ. Обновите страницу и попробуйте снова.' })}</Alert>}
    </Box>
  );
}

/** Compact storage destinations; connection details stay on their detail page. */
export function StorageSettingsPanel() {
  const { t } = useTranslation();
  const tokens = useTokens();
  const { isAdmin } = useRole();
  const { data: warehouses, isLoading, error } = useWarehouses();
  const setActive = useSetWarehouseActive();
  const [pendingId, setPendingId] = useState(null);
  const [toDisable, setToDisable] = useState(null);
  const [dialogError, setDialogError] = useState(null);
  const [snackError, setSnackError] = useState(null);

  const runSetActive = async (warehouse, active) => {
    if (!isAdmin) return;
    setPendingId(warehouse.public_id);
    try {
      await setActive.mutateAsync({ id: warehouse.public_id, isActive: active });
      setToDisable(null);
    } catch (err) {
      const message = extractApiError(err, t('data.failedToUpdateWarehouse'));
      if (active) setSnackError(message);
      else setDialogError(message);
    } finally {
      setPendingId(null);
    }
  };

  const toggle = (warehouse) => {
    if (!isAdmin) return;
    if (warehouse.is_active) {
      setDialogError(null);
      setToDisable(warehouse);
    } else runSetActive(warehouse, true);
  };

  const rows = (warehouses || []).filter((warehouse) => ['lakehouse', 'byo'].includes(warehouse.kind));
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {error ? <Alert severity="error">{t('data.failedToLoadWarehouses')}</Alert> : isLoading ? (
        <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={24} /></Box>
      ) : rows.length ? (
        <Box>
          {rows.map((warehouse) => {
            const managed = warehouse.kind === 'lakehouse';
            const Icon = managed ? WavesIcon : StorageIcon;
            return (
              <Box key={warehouse.public_id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 1.75, flexWrap: 'wrap', borderBottom: `1px solid ${tokens.border}`, '&:first-of-type': { pt: 0 } }}>
                <Icon sx={{ color: tokens.iconMuted, flexShrink: 0 }} />
                <Box sx={{ flex: '1 1 200px', minWidth: 0 }}>
                  <Typography variant="body2" fontWeight={600} sx={{ overflowWrap: 'anywhere' }}>{warehouse.name}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {managed ? t('data.workspaceData.managedStorage', { defaultValue: 'Хранением управляет Evolve' }) : warehouse.engine_display}
                  </Typography>
                </Box>
                <Chip size="small" label={warehouse.is_active ? t('data.workspaceData.storageAvailable', { defaultValue: 'Включено' }) : t('data.disabled')} color={warehouse.is_active ? 'default' : 'warning'} />
                <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                  <Button size="small" component={RouterLink} to={`/console/pipelines/storage/${warehouse.public_id}${managed && isAdmin ? '?tab=settings' : ''}`}>
                    {isAdmin ? t('data.settings') : t('data.overview')}
                  </Button>
                  {isAdmin && !managed && <Button size="small" color={warehouse.is_active ? 'warning' : 'success'} disabled={pendingId === warehouse.public_id} onClick={() => toggle(warehouse)} aria-label={`${warehouse.is_active ? t('data.disable') : t('data.enable')} ${warehouse.name}`}>
                    {pendingId === warehouse.public_id ? <CircularProgress size={16} /> : warehouse.is_active ? t('data.disable') : t('data.enable')}
                  </Button>}
                </Box>
              </Box>
            );
          })}
        </Box>
      ) : <Typography variant="body2" color="text.secondary">{t('data.noWarehousesYet')}</Typography>}
      {isAdmin && <StorageQuotaControls />}

      <ConfirmActionDialog
        open={!!toDisable && isAdmin}
        title={toDisable?.dependents?.blocking ? t('data.canTDisableWarehouse') : t('data.disableWarehouse')}
        confirmWord={t('data.actiondisable')}
        confirmLabel={t('data.disableWarehouse')}
        confirmColor="warning"
        blocked={!!toDisable?.dependents?.blocking}
        loading={!!toDisable && pendingId === toDisable.public_id}
        error={dialogError}
        onConfirm={() => runSetActive(toDisable, false)}
        onClose={() => setToDisable(null)}
      >
        {toDisable?.dependents?.blocking ? (
          <Typography variant="body2"><strong>{toDisable?.name}</strong>{' '}{t('data.isStillUsedBy')}{' '}{describeDependents(toDisable?.dependents)}{t('data.disableOrRemoveThoseFirstThenYouCanDisableThisWarehouse')}</Typography>
        ) : (
          <Typography variant="body2">{t('data.disabling')}{' '}<strong>{toDisable?.name}</strong>{' '}{t('data.hidesItFromNewPipelinesAndDbtProjectsAndFreesIts')}</Typography>
        )}
      </ConfirmActionDialog>
      <Snackbar open={!!snackError} autoHideDuration={6000} onClose={() => setSnackError(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity="error" variant="filled" onClose={() => setSnackError(null)}>{snackError}</Alert>
      </Snackbar>
    </Box>
  );
}

/** Preparation stays next to the loads that feed it; Git details are secondary. */
export function PreparationPanel() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { hash } = useLocation();
  const { data: projects, isLoading, error } = useDbtProjects();
  const requestedProject = searchParams.get('project');
  const selected = requestedProject
    ? (projects || []).find((project) => project.public_id === requestedProject)
    : (projects || []).find((project) => project.is_active) || projects?.[0];
  const selectProject = (id) => {
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'preparation');
    if (id) next.set('project', id);
    else next.delete('project');
    next.delete('history');
    navigate({ search: `?${next}`, hash });
  };
  if (error) return <Alert severity="error">{t('data.failedToLoadDbtProjects')}</Alert>;
  if (isLoading) return <Box sx={{ py: 6, textAlign: 'center' }}><CircularProgress /></Box>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {!projects?.length ? (
        <Paper sx={{ p: { xs: 3, sm: 4 } }}>
          <Typography variant="h6" fontWeight={600}>{t('data.workspaceData.noPreparation', { defaultValue: 'Подготовка ещё не настроена' })}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t('data.workspaceData.noPreparationBody', { defaultValue: 'Проект подготовки создаётся платформой после настройки загрузки данных.' })}</Typography>
        </Paper>
      ) : <>
        {projects.length > 1 && (
          <TextField
            select size="small" label={t('data.workspacePreparation.project')}
            value={selected?.public_id || ''} onChange={(event) => selectProject(event.target.value)}
            sx={{ width: { xs: '100%', sm: 360 }, maxWidth: '100%' }}
          >
            {projects.map((project) => <MenuItem key={project.public_id} value={project.public_id}>{project.pipeline_name || project.name}</MenuItem>)}
          </TextField>
        )}
        {selected ? <PreparationWorkspace key={selected.public_id} projectId={selected.public_id} embedded /> : (
          <Alert severity="warning" action={<Button color="inherit" onClick={() => selectProject(null)}>{t('data.workspacePreparation.chooseProject')}</Button>}>
            {t('data.workspacePreparation.projectUnavailable')}
          </Alert>
        )}
      </>}
    </Box>
  );
}
